import { Link } from '@tanstack/react-router'

import { NAVIGATION, type NavItem } from '@/app/navigation'
import { useAuth } from '@/auth/use-auth'
import { cn } from '@/lib/utils'

/**
 * The permission-driven navigation tree.
 *
 * An item is rendered only when the user holds the permission it names, and a parent whose
 * children are all hidden disappears with them, so no section stands empty. This is presentation:
 * the server is the enforcer, and a user who navigates directly to a hidden route still meets its
 * permission check on the first request the page makes.
 *
 * The same tree serves the dense rail and the drawer. `size` is the only difference between them:
 * a rail read from a chair at a counter and a drawer worked with a thumb want different row
 * heights, and nothing else about the navigation should change with the width of the window.
 */
export type SidebarNavSize = 'dense' | 'touch'

export function SidebarNav({
  collapsed,
  size = 'dense',
}: {
  collapsed: boolean
  size?: SidebarNavSize
}) {
  const { hasPermission, hasAnyPermission } = useAuth()

  const isVisible = (item: NavItem) =>
    (item.permission === undefined || hasPermission(item.permission)) &&
    (item.anyOf === undefined || hasAnyPermission(item.anyOf))

  return (
    <nav
      className={cn('flex-1 space-y-4 overflow-y-auto px-2 py-3', size === 'touch' && 'space-y-5 pb-6')}
      aria-label="Main"
    >
      {NAVIGATION.map((section) => {
        const items = section.items.filter(isVisible)
        if (items.length === 0) {
          return null
        }

        return (
          <div key={section.label}>
            {!collapsed ? (
              <p className="px-2 pb-1 text-2xs font-semibold tracking-widest text-sidebar-muted uppercase">
                {section.label}
              </p>
            ) : null}

            <ul className="space-y-0.5">
              {items.map((item) => {
                const children = item.children?.filter(isVisible) ?? []

                return (
                  <li key={item.to}>
                    <NavLink item={item} collapsed={collapsed} size={size} />

                    {!collapsed && children.length > 0 ? (
                      <ul className="mt-0.5 ml-4 space-y-0.5 border-l border-sidebar-border pl-2">
                        {children.map((child) => (
                          <li key={child.to}>
                            <NavLink item={child} collapsed={false} size={size} nested />
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}
    </nav>
  )
}

function NavLink({
  item,
  collapsed,
  size,
  nested = false,
}: {
  item: NavItem
  collapsed: boolean
  size: SidebarNavSize
  nested?: boolean
}) {
  const Icon = item.icon
  const touch = size === 'touch'

  return (
    <Link
      to={item.to}
      title={collapsed ? item.label : undefined}
      // A parent route stays highlighted while a child is open, so the operator never loses
      // track of which module they are inside.
      activeOptions={{ exact: item.to === '/' }}
      className={cn(
        'group relative flex items-center gap-2.5 rounded-control px-2 py-1.5 text-sm text-sidebar-muted transition-colors',
        'hover:bg-sidebar-hover hover:text-sidebar-text',
        nested && 'text-xs',
        collapsed && 'justify-center px-0',
        touch && 'min-h-11 px-3',
        touch && nested && 'text-sm',
      )}
      activeProps={{
        className: cn(
          'bg-sidebar-active text-sidebar-active-text font-medium',
          'before:absolute before:inset-y-1 before:left-0 before:w-0.5 before:rounded-full before:bg-brand-fill',
        ),
      }}
    >
      <Icon className={cn('shrink-0', nested && !touch ? 'size-3.5' : 'size-4')} aria-hidden />
      {!collapsed ? <span className="truncate">{item.label}</span> : null}
    </Link>
  )
}
